import type { PortableTextBlock } from '@portabletext/react';

/**
 * This should contain any potential block types that are used in the
 * PortableText schema (including any custom types)
 */
const blockTypes = ['block', 'button', 'iconsTextVertical', 'image', 'loyaltyTiers', 'span'];

/**
 * Check if an object is a PortableTextBlock
 * @param obj - The object to check if it is a PortableTextBlock
 * @returns - True if the object is a PortableTextBlock, false otherwise
 */
export const isPortableTextBlock = (obj: unknown): obj is PortableTextBlock => {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '_type' in obj &&
    typeof obj._type === 'string' &&
    blockTypes.includes(obj._type)
  );
};

/**
 * Check if a PortableTextBlock is clean
 * A PortableTextBlock is considered clean if it does not have multiple links in a single span
 * or if it has no children. This is to prevent issues with nested HTML elements in the PortableTextBlock. This can occur when
 * an author accidentally copies and pastes content into the PortableText editor where the content contains
 * nested HTML elements. For example if an author copies from a word document or a web page.
 * @param obj - The PortableTextBlock to check if it is clean
 * @returns True if the PortableTextBlock is clean, false otherwise
 */
export const isCleanPortableTextBlock = (obj: PortableTextBlock) => {
  // Check that if a block has children where there is a span that has multiple marks that the marks are both not links
  // If a block has children, we check if all children are clean
  if (obj?.children?.length) {
    return obj.children.every((child) => {
      if (child._type === 'span' && child.marks.length > 1) {
        const markDefs = obj.markDefs ?? [];

        // Filter marks to only those that are in the current child
        const marks = markDefs.filter((markDef) => child.marks.includes(markDef._key));

        // If more than one mark is a link, we assume it's not clean since we don't allow multiple links in a single span
        return marks.filter((mark) => mark && mark._type === 'link').length <= 1;
      }

      // If the child is not a span, we assume it's clean
      return true;
    });
  }

  // If there are no children, we assume it's clean
  return true;
};

/**
 * Check if an array is an array of PortableTextBlock
 * @param arr - The array to check if it is an array of PortableTextBlock
 * @returns - True if the array is an array of PortableTextBlock, false otherwise
 */
export const isPortableTextBlockArray = (arr: unknown): arr is PortableTextBlock[] => {
  return Array.isArray(arr) && arr.every((item) => isPortableTextBlock(item) && isCleanPortableTextBlock(item));
};

/**
 * Find and replace a string in a PortableTextBlock array
 * @param arr - The PortableTextBlock array to search through
 * @param find - The string to find
 * @param replace - The string to replace the found string with
 * @returns - The PortableTextBlock array with the found strings replaced
 */
export const findAnReplacePortableText = (
  arr: PortableTextBlock[],
  find: string,
  replace: string,
): PortableTextBlock[] => {
  return arr.map((block) => {
    if (block.children) {
      block.children = block.children.map((child) => {
        if (child.text.includes(find)) {
          child.text = child.text.replace(find, replace);
        }
        return child;
      });
    }
    return block;
  });
};
